import { createStore, get, set } from 'idb-keyval';

import type { AnnotationBridgeDocument, AnnotationEntry } from './types';

const memoryStore = new Map<string, AnnotationBridgeDocument>();

export interface AnnotationBridgeStore {
  read(tenantId: string, projectId: string): Promise<AnnotationBridgeDocument>;
  write(tenantId: string, projectId: string, document: AnnotationBridgeDocument): Promise<void>;
  appendAnnotation(
    tenantId: string,
    projectId: string,
    annotation: AnnotationEntry,
    actor: string,
  ): Promise<AnnotationBridgeDocument>;
}

export function createAnnotationBridgeStore(): AnnotationBridgeStore {
  const supportsIndexedDb = typeof indexedDB !== 'undefined';
  const store = supportsIndexedDb ? createStore('geoviewer-annotations', 'bridge-v1') : null;

  return {
    async read(tenantId, projectId) {
      const key = bridgeKey(tenantId, projectId);
      const existing = store
        ? await get<AnnotationBridgeDocument>(key, store)
        : memoryStore.get(key);
      return existing ?? emptyDocument();
    },
    async write(tenantId, projectId, document) {
      const key = bridgeKey(tenantId, projectId);
      if (store) {
        await set(key, document, store);
      } else {
        memoryStore.set(key, document);
      }
    },
    async appendAnnotation(tenantId, projectId, annotation, actor) {
      const current = await this.read(tenantId, projectId);
      const next: AnnotationBridgeDocument = {
        annotations: [...current.annotations, annotation],
        view: current.view,
        lineage: [
          ...current.lineage,
          {
            ts: new Date(0).toISOString(),
            actor,
            action: 'annotation.append',
            annotationId: annotation.id,
          },
        ],
      };
      await this.write(tenantId, projectId, next);
      return next;
    },
  };
}

export function bridgeKey(tenantId: string, projectId: string): string {
  return `${tenantId}:${projectId}`;
}

export function clearAnnotationBridgeMemoryForTests(): void {
  memoryStore.clear();
}

function emptyDocument(): AnnotationBridgeDocument {
  return {
    annotations: [],
    view: null,
    lineage: [],
  };
}
